import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { plansAPI } from '../services/api';
import '../styles/Plans.css';

const FALLBACK_PLANS = [
  {
    id: 1, name: { en: 'Explorer', fr: 'Explorateur' },
    description: { en: 'Everything you need to plan and enjoy your first trip to Istanbul.', fr: 'Tout ce qu\'il faut pour préparer et réussir votre premier voyage à Istanbul.' },
    price: 6500, currency: 'XAF', duration: 30, accessLevel: 'basic',
    features: { en: ['15 travel guides', 'Interactive visa checker by passport', 'Visa refusal: reasons & how to reapply', 'Airport, transit & free Touristanbul tour', 'Istanbulkart & 2026 transport fares', 'Attractions with 2026 entrance fees', 'Where to stay — neighborhoods', 'Food & halal dining with real prices', 'Weather, packing & 2027 holidays', 'Health, pharmacies & insurance', 'Money, SIM, apps & customs rules', 'Safety, scams & etiquette', 'Email support 72h'], fr: ['15 guides de voyage', 'Vérificateur de visa interactif par passeport', 'Visa refusé : causes & nouvelle demande', 'Aéroport, transit & visite gratuite Touristanbul', 'Istanbulkart & tarifs transport 2026', 'Sites avec tarifs d\'entrée 2026', 'Où loger — quartiers', 'Cuisine & repas halal avec vrais prix', 'Météo, valise & jours fériés 2027', 'Santé, pharmacies & assurance', 'Argent, SIM, applis & douane', 'Sécurité, arnaques & savoir-vivre', 'Support email 72h'] }
  },
  {
    id: 2, name: { en: 'Navigator', fr: 'Navigateur' },
    description: { en: 'The complete Istanbul toolkit — itineraries, budgets, districts and day trips.', fr: 'La boîte à outils complète d\'Istanbul — itinéraires, budgets, quartiers et excursions.' },
    price: 16500, currency: 'XAF', duration: 90, accessLevel: 'premium',
    features: { en: ['Everything in Explorer, plus:', 'All 24 travel guides', 'Budget calculator (TL / EUR / XAF)', '3, 5 & 7-day itineraries', 'Cappadocia extension & balloon prices', 'Bosphorus, islands & day trips', 'Hammam guide with 2026 prices', 'Mosques & Ramadan in Istanbul', 'Grand Bazaar, bargaining & tax-free', 'Studying in Istanbul — complete guide', 'Medical tourism: prices & safe clinics', 'Families, women & solo travellers', 'Everyday price list & district comparison', 'Email support 24h'], fr: ['Tout de l\'Explorateur, plus :', 'Les 24 guides de voyage', 'Calculateur de budget (TL / EUR / XAF)', 'Itinéraires 3, 5 & 7 jours', 'Extension Cappadoce & prix des montgolfières', 'Bosphore, îles & excursions', 'Guide des hammams avec prix 2026', 'Mosquées & Ramadan à Istanbul', 'Grand Bazar, marchandage & détaxe', 'Étudier à Istanbul — guide complet', 'Tourisme médical : prix & cliniques sûres', 'Familles, femmes & voyageurs solo', 'Liste de prix & comparatif des quartiers', 'Support email 24h'] }
  },
  {
    id: 3, name: { en: 'Pathfinder', fr: 'Pionnier' },
    description: { en: 'Full-year access for traders, investors and long-stay visitors.', fr: 'Accès annuel complet pour commerçants, investisseurs et longs séjours.' },
    price: 39500, currency: 'XAF', duration: 365, accessLevel: 'enterprise',
    features: { en: ['Everything in Navigator, plus:', 'All 30 guides', 'Wholesale sourcing: Laleli, Merter, Osmanbey', 'Supplier & cargo-to-Africa checklist', 'Trade-fair calendar 2026–2027', 'Opening a company in Türkiye', 'Work permits & 2026 salary rules', 'Buying property step by step', 'Banking, tax number & safe payments', 'Residence permits & citizenship by investment', 'Turkish phrase trainer', 'Trip & business roadmap', 'Email support 4h'], fr: ['Tout du Navigateur, plus :', 'Les 30 guides', 'Achats en gros : Laleli, Merter, Osmanbey', 'Checklist fournisseurs & cargo vers l\'Afrique', 'Calendrier des salons 2026–2027', 'Créer une société en Türkiye', 'Permis de travail & règles salariales 2026', 'Acheter un bien étape par étape', 'Banque, numéro fiscal & paiements sûrs', 'Permis de séjour & citoyenneté par investissement', 'Entraîneur de phrases turques', 'Feuille de route voyage & affaires', 'Support email 4h'] }
  }
];

const DETAILS = {
  basic: {
    title: { en: '🔍 Explorer', fr: '🔍 Explorateur' },
    desc: { en: 'A strong start for your first trip to Istanbul.', fr: 'Un excellent départ pour votre premier voyage à Istanbul.' },
    items: { en: ['15 travel guides', 'Interactive visa checker', 'Visa refusal guide', 'Airport, transit & Touristanbul', 'Top attractions & 2026 prices', 'Food, health & customs rules', 'Money, SIM, apps & safety'], fr: ['15 guides de voyage', 'Vérificateur de visa interactif', 'Guide visa refusé', 'Aéroport, transit & Touristanbul', 'Sites & prix 2026', 'Cuisine, santé & douane', 'Argent, SIM, applis & sécurité'] },
    footer: { en: 'Best for first-time visitors', fr: 'Idéal pour une première visite' },
  },
  premium: {
    title: { en: '🧭 Navigator', fr: '🧭 Navigateur' },
    desc: { en: 'More depth, more tools, a smoother trip.', fr: 'Plus de détails, plus d\'outils, un voyage plus fluide.' },
    items: { en: ['All 24 travel guides', 'Budget calculator', 'Ready-made itineraries', 'Cappadocia & day trips', 'Hammams, mosques & Ramadan', 'Shopping, study & medical', 'Price list & district comparison'], fr: ['Les 24 guides de voyage', 'Calculateur de budget', 'Itinéraires prêts à l\'emploi', 'Cappadoce & excursions', 'Hammams, mosquées & Ramadan', 'Shopping, études & santé', 'Liste de prix & comparatif quartiers'] },
    footer: { en: 'Most Popular', fr: 'Le plus populaire' },
  },
  enterprise: {
    title: { en: '🗺️ Pathfinder', fr: '🗺️ Pionnier' },
    desc: { en: 'Full-year access for business and long stays.', fr: 'Accès d\'un an pour les affaires et les longs séjours.' },
    items: { en: ['All 30 guides', 'Wholesale sourcing & cargo to Africa', 'Trade-fair calendar', 'Company setup & work permits', 'Buying property & banking', 'Residence & citizenship by investment', 'Turkish phrase trainer'], fr: ['Les 30 guides', 'Achats en gros & cargo vers l\'Afrique', 'Calendrier des salons', 'Création de société & permis de travail', 'Achat immobilier & banque', 'Séjour & citoyenneté par investissement', 'Entraîneur de phrases turques'] },
    footer: { en: 'Full-year access', fr: 'Accès d\'un an' },
  },
};

const Plans = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await plansAPI.getAll();
      const raw = Array.isArray(response.data) ? response.data : response.data?.plans || [];
      setPlans(raw.length > 0 ? raw : FALLBACK_PLANS);
    } catch {
      setPlans(FALLBACK_PLANS);
    } finally {
      setLoading(false);
    }
  };

  const getLang = (obj) => obj?.[lang] || obj?.en || '';

  const getType = (plan) => {
    if (plan.accessLevel) return plan.accessLevel;
    if (plan.price <= 10000) return 'basic';
    if (plan.price <= 25000) return 'premium';
    return 'enterprise';
  };

  const getIcon = (type) => ({ basic: '🔍', premium: '🧭', enterprise: '🗺️' }[type] || '🔍');

  const formatPrice = (plan) => {
    const n = Number(plan.price);
    if (Number.isNaN(n)) return plan.price;
    return plan.currency === 'XAF' ? Math.round(n).toLocaleString() : n.toFixed(2);
  };

  const handleSelect = (plan) => {
    const name = getLang(plan.name) || 'Explorer';
    navigate(`/plan-selection?plan=${encodeURIComponent(name)}&level=${getType(plan)}&price=${plan.price}&currency=${plan.currency || 'XAF'}`);
  };

  if (loading) return <div className="loading">{t('loading')}</div>;

  return (
    <section className="plans-container">
      <div className="plans-header">
        <h2>{t('plans_title')}</h2>
        <p>{t('plans_description')}</p>
      </div>

      <div className="plans-trust">
        <div className="trust-item"><span>✅</span><span>{t('trust_instant')}</span></div>
        <div className="trust-item"><span>🔒</span><span>{t('trust_secure')}</span></div>
        <div className="trust-item"><span>🌐</span><span>{t('trust_bilingual')}</span></div>
        <div className="trust-item"><span>🕌</span><span>{t('trust_clients')}</span></div>
      </div>

      <div className="plans-grid">
        {plans.map((plan) => {
          const type = getType(plan);
          return (
            <div key={plan.id} className={`plan-card plan-${type}`}>
              {type === 'premium' && <div className="most-popular-badge">{t('most_popular')}</div>}
              <div className="plan-icon">{getIcon(type)}</div>
              <h3>{getLang(plan.name)}</h3>
              <p className="plan-description">{getLang(plan.description)}</p>
              <div className="plan-price">
                <span className="amount">{formatPrice(plan)}</span>
                <span className="period">{plan.currency || 'XAF'} / {plan.duration} {t('plan_duration')}</span>
              </div>
              <ul className="plan-features">
                {(plan.features?.[lang] || plan.features?.en || []).map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
              <button className="select-plan-btn" onClick={() => handleSelect(plan)}>{t('plan_select')}</button>
            </div>
          );
        })}
      </div>

      <div className="plans-details">
        <h2 className="details-title">{lang === 'fr' ? 'Ce que comprend chaque forfait' : 'What Each Plan Includes'}</h2>
        <p className="details-subtitle">{lang === 'fr' ? 'Choisissez le forfait adapté à votre voyage.' : 'Choose the plan that fits your trip.'}</p>
        <div className="details-grid">
          {['basic', 'premium', 'enterprise'].map(type => (
            <div key={type} className={`details-card ${type}`}>
              <h3>{DETAILS[type].title[lang]}</h3>
              <p className="details-desc">{DETAILS[type].desc[lang]}</p>
              <ul>
                {DETAILS[type].items[lang].map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <div className={`details-footer ${type === 'premium' ? 'highlight' : ''}`}>{DETAILS[type].footer[lang]}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Plans;
